import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IDEE_STATUS_NAMEN, UEBERNAHME_NAMEN, euro, type Idee, type IdeenZiele, type UebernahmeArt } from '@beholder/shared/module/ideen';
import { api, ApiFehler } from '../../core/api';
import { Auswahl, Dialog, Eingabe, Feld, Knopf, Meldung, Textfeld, useAktion } from '../../core/ui';
import { pfad, SEITE } from './daten';
import { Zeichen } from './Text';

export interface IdeeFormular {
  titel: string;
  beschreibung: string;
  kosten: string;
  kostenEuro: string;
  aufwand: string;
  risiken: string;
}

export const LEER: IdeeFormular = { titel: '', beschreibung: '', kosten: '', kostenEuro: '', aufwand: '', risiken: '' };

function ausIdee(i: Idee): IdeeFormular {
  return {
    titel: i.titel,
    beschreibung: i.beschreibung,
    kosten: i.kosten,
    kostenEuro: i.kostenEuro === null ? '' : String(i.kostenEuro).replace('.', ','),
    aufwand: i.aufwand,
    risiken: i.risiken,
  };
}

export function zahlAus(t: string): number | null {
  const s = t.replace(/[€\s]/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.');
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

/** Dialog zum Anlegen oder Bearbeiten einer Idee. Beim Bearbeiten mit Versionsprüfung (keine stillen Überschreibungen). */
export function IdeeDialog({
  offen,
  schliessen,
  titel,
  start,
  speichern,
}: {
  offen: boolean;
  schliessen(): void;
  titel: string;
  start: IdeeFormular;
  speichern(f: IdeeFormular & { kostenZahl: number | null }, trotzdem: boolean): Promise<void>;
}) {
  const [f, setF] = useState(start);
  const [konflikt, setKonflikt] = useState(false);
  const a = useAktion();
  useEffect(() => {
    if (offen) {
      setF(start);
      setKonflikt(false);
      a.setFehler('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen]);
  const setze = (k: keyof IdeeFormular) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const los = async (trotzdem = false) => {
    const kostenZahl = zahlAus(f.kostenEuro);
    if (Number.isNaN(kostenZahl)) return a.setFehler('Kosten in Euro bitte als Zahl, z. B. 250 oder 1.250,50.');
    if (!f.titel.trim()) return a.setFehler('Die Idee braucht einen Titel.');
    try {
      await a.ausfuehren(async () => {
        try {
          await speichern({ ...f, kostenZahl }, trotzdem);
          schliessen();
        } catch (e) {
          if (e instanceof ApiFehler && e.status === 409) setKonflikt(true);
          else throw e;
        }
      });
    } catch {
      /* Fehler steht in a.fehler */
    }
  };
  return (
    <Dialog
      titel={titel}
      offen={offen}
      schliessen={schliessen}
      breit
      fuss={
        <>
          <Knopf art="haupt" disabled={a.laeuft} onClick={() => void los()}>
            Speichern
          </Knopf>
          <Knopf onClick={schliessen}>Abbrechen</Knopf>
        </>
      }
    >
      <form
        className="stapel"
        onSubmit={(e) => {
          e.preventDefault();
          void los();
        }}
      >
        {konflikt && (
          <Meldung art="info">
            Jemand anderes hat diese Idee gerade geändert.{' '}
            <Knopf klein onClick={() => void los(true)}>
              Meine Fassung trotzdem speichern
            </Knopf>{' '}
            <Knopf klein art="leise" onClick={schliessen}>
              Die neue Fassung behalten
            </Knopf>
          </Meldung>
        )}
        <Meldung>{a.fehler}</Meldung>
        <Feld titel="Titel">
          <Eingabe value={f.titel} onChange={setze('titel')} maxLength={200} required />
        </Feld>
        <Feld titel="Beschreibung">
          <Textfeld value={f.beschreibung} onChange={setze('beschreibung')} rows={4} maxLength={5000} />
        </Feld>
        <div className="id-raster-2">
          <Feld titel="Kosten (grob)" hinweis="z. B. „ca. 300 €“">
            <Eingabe value={f.kosten} onChange={setze('kosten')} maxLength={200} />
          </Feld>
          <Feld titel="Kosten in Euro" hinweis="für den Kostenvorschlag im Budget">
            <Eingabe value={f.kostenEuro} onChange={setze('kostenEuro')} inputMode="decimal" placeholder="z. B. 300" />
          </Feld>
        </div>
        <Feld titel="Aufwand (grob)" hinweis="z. B. „1 Tag, 2 Leute“">
          <Eingabe value={f.aufwand} onChange={setze('aufwand')} maxLength={200} />
        </Feld>
        <Feld titel="Risiken">
          <Textfeld value={f.risiken} onChange={setze('risiken')} rows={2} maxLength={2000} />
        </Feld>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

const ZIEL_PFAD: Record<UebernahmeArt, (p: string, id: string) => string> = {
  aufgabe: (p, id) => `/p/${p}/aufgaben/${id}`,
  mindmap: (p) => `/p/${p}/mindmap`,
  budget: (p) => `/p/${p}/budget/vorschlaege`,
  projekt: (p) => `/p/${p}`,
};

/** Karte einer Idee mit Stimmen, Festhalten/Verwerfen, Bearbeiten, Übernehmen, Löschen. */
export function IdeeKarte({
  idee,
  ichId,
  darfSchreiben,
  ziele,
  mitRunde,
  neu,
}: {
  idee: Idee;
  ichId: string;
  darfSchreiben: boolean;
  ziele: IdeenZiele | null;
  mitRunde?: boolean;
  /** frisch von der KI → kurz hervorheben */
  neu?: boolean;
}) {
  const a = useAktion();
  // Stand beim Öffnen des Dialogs merken – sonst würde eine Live-Änderung anderer still überschrieben
  const [bearbeiten, setBearbeiten] = useState<Idee | null>(null);
  const [menue, setMenue] = useState(false);
  const [uebernahme, setUebernahme] = useState<'mindmap' | 'budget' | 'projekt' | null>(null);
  const [offen, setOffen] = useState(false);
  const [hinweis, setHinweis] = useState('');
  const menueRef = useRef<HTMLDivElement>(null);
  const meine = idee.stimmen.find((s) => s.benutzerId === ichId)?.wert ?? 0;
  const hoch = idee.stimmen.filter((s) => s.wert > 0);
  const runter = idee.stimmen.filter((s) => s.wert < 0);
  const ziel = (rest: string) => pfad(`/ideen/${idee.id}${rest}`);
  const ziele2 = (['aufgabe', 'mindmap', 'budget'] as const).filter((z) => ziele?.[z]);
  const inProjekt = !!ziele && (ziele.projektNeu || ziele.projekte.some((p) => p.id !== idee.projektId));

  useEffect(() => {
    if (!menue) return;
    const zu = (e: MouseEvent) => !menueRef.current?.contains(e.target as Node) && setMenue(false);
    document.addEventListener('mousedown', zu);
    return () => document.removeEventListener('mousedown', zu);
  }, [menue]);

  const stimme = (w: 1 | -1) => void a.ausfuehren(() => api.put(ziel('/stimme'), { wert: meine === w ? 0 : w }));
  const status = (s: Idee['status']) => void a.ausfuehren(() => api.patch(ziel(''), { status: s }));
  const loeschen = () => {
    if (!confirm(`Die Idee „${idee.titel}“ in den Papierkorb legen? (Sie lässt sich 180 Tage lang zurückholen.)`)) return;
    void a.ausfuehren(() => api.delete(ziel('')));
  };
  const uebernehmen = async (art: UebernahmeArt, extra: Record<string, unknown> = {}) => {
    const r = await a.ausfuehren(() => api.post<{ pfad: string }>(ziel('/uebernehmen'), { ziel: art, ...extra }));
    if (r) {
      setHinweis(
        art === 'projekt'
          ? 'Ins Projekt übernommen (Unterreiter „Übernommene Ideen“).'
          : art === 'aufgabe'
            ? 'Als Vorschlag in Aufgaben abgelegt.'
            : art === 'budget'
              ? 'Als Kostenvorschlag im Budget abgelegt (unter „Vorschläge“).'
              : 'Als Mindmap-Punkt angelegt.',
      );
    }
    return r;
  };

  const lang = idee.beschreibung.length > 220;

  return (
    <article className={`id-idee id-status-${idee.status}${neu ? ' id-frisch' : ''}`}>
      <header className="id-idee-kopf">
        <h4 title={idee.titel}>{idee.titel}</h4>
        {idee.status !== 'neu' && <span className={`id-marke id-marke-${idee.status}`}>{IDEE_STATUS_NAMEN[idee.status]}</span>}
      </header>
      {mitRunde && (
        <Link className="id-idee-runde klein" to={`${SEITE}/${idee.sitzungId}`}>
          aus „{idee.sitzungTitel}“{idee.projektName ? ` · ${idee.projektName}` : ''}
        </Link>
      )}
      {idee.beschreibung && <p className={`id-idee-text${lang && !offen ? ' id-zeilen-3' : ''}`}>{idee.beschreibung}</p>}
      <dl className="id-idee-werte">
        {(idee.kosten || idee.kostenEuro !== null) && (
          <>
            <dt>Kosten</dt>
            <dd>
              {idee.kosten || euro(idee.kostenEuro!)}
              {idee.kosten && idee.kostenEuro !== null && !idee.kosten.includes(String(Math.round(idee.kostenEuro))) && (
                <span className="leise"> ({euro(idee.kostenEuro)})</span>
              )}
            </dd>
          </>
        )}
        {idee.aufwand && (
          <>
            <dt>Aufwand</dt>
            <dd>{idee.aufwand}</dd>
          </>
        )}
        {idee.risiken && (
          <>
            <dt>Risiken</dt>
            <dd>{idee.risiken}</dd>
          </>
        )}
      </dl>
      {lang && (
        <button type="button" className="id-mehr" onClick={() => setOffen((o) => !o)}>
          {offen ? 'weniger' : 'mehr anzeigen'}
        </button>
      )}
      {idee.uebernommen.length > 0 && (
        <div className="id-uebernommen">
          {idee.uebernommen.map((u, i) => (
            <Link
              key={i}
              to={ZIEL_PFAD[u.art](u.projektId ?? idee.projektId ?? u.id, u.id)}
              className="id-marke id-marke-ziel"
              title={`${u.von ?? ''}, ${new Date(u.am).toLocaleDateString('de-DE')}`}
            >
              {Zeichen.pfeil} {u.art === 'projekt' ? `Projekt „${u.name ?? ''}“` : UEBERNAHME_NAMEN[u.art]}
            </Link>
          ))}
        </div>
      )}
      <Meldung>{a.fehler}</Meldung>
      {hinweis && <Meldung art="ok">{hinweis}</Meldung>}
      <footer className="id-idee-fuss">
        <div className="id-stimmen">
          <button
            type="button"
            className={`id-daumen${meine > 0 ? ' an' : ''}`}
            disabled={!darfSchreiben || a.laeuft}
            onClick={() => stimme(1)}
            title={hoch.length ? `Dafür: ${hoch.map((s) => s.name).join(', ')}` : 'Daumen hoch'}
            aria-pressed={meine > 0}
          >
            {Zeichen.hoch} {hoch.length || ''}
          </button>
          <button
            type="button"
            className={`id-daumen id-daumen-runter${meine < 0 ? ' an' : ''}`}
            disabled={!darfSchreiben || a.laeuft}
            onClick={() => stimme(-1)}
            title={runter.length ? `Dagegen: ${runter.map((s) => s.name).join(', ')}` : 'Daumen runter'}
            aria-pressed={meine < 0}
          >
            {Zeichen.runter} {runter.length || ''}
          </button>
        </div>
        <span className="abstand" />
        {darfSchreiben && (
          <>
            {idee.status !== 'festgehalten' && (
              <Knopf klein onClick={() => status('festgehalten')} disabled={a.laeuft} title="Idee festhalten">
                Festhalten
              </Knopf>
            )}
            {idee.status !== 'verworfen' ? (
              <Knopf klein art="leise" onClick={() => status('verworfen')} disabled={a.laeuft}>
                Verwerfen
              </Knopf>
            ) : (
              <Knopf klein art="leise" onClick={() => status('neu')} disabled={a.laeuft}>
                Wieder offen
              </Knopf>
            )}
            <div className="id-menue-halter" ref={menueRef}>
              <Knopf klein art="leise" aria-label="Weitere Aktionen" aria-expanded={menue} onClick={() => setMenue((m) => !m)}>
                ⋯
              </Knopf>
              {menue && (
                <div className="id-menue" role="menu">
                  <button type="button" role="menuitem" onClick={() => (setMenue(false), setBearbeiten(idee))}>
                    {Zeichen.stift} Bearbeiten
                  </button>
                  {ziele2.map((z) => (
                    <button
                      key={z}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMenue(false);
                        if (z === 'aufgabe') void uebernehmen('aufgabe');
                        else setUebernahme(z);
                      }}
                    >
                      {Zeichen.pfeil} {z === 'aufgabe' ? 'Als Aufgabe vorschlagen' : `Als ${UEBERNAHME_NAMEN[z]} übernehmen`}
                    </button>
                  ))}
                  {inProjekt && (
                    <button type="button" role="menuitem" onClick={() => (setMenue(false), setUebernahme('projekt'))}>
                      {Zeichen.pfeil} In ein Projekt übernehmen …
                    </button>
                  )}
                  <button type="button" role="menuitem" className="id-gefahr" onClick={() => (setMenue(false), loeschen())}>
                    {Zeichen.x} In den Papierkorb
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </footer>

      <IdeeDialog
        offen={!!bearbeiten}
        schliessen={() => setBearbeiten(null)}
        titel="Idee bearbeiten"
        start={ausIdee(bearbeiten ?? idee)}
        speichern={async (f, trotzdem) => {
          const alt = bearbeiten ?? idee;
          // nur geänderte Felder schicken
          const neu = { titel: f.titel, beschreibung: f.beschreibung, kosten: f.kosten, kostenEuro: f.kostenZahl, aufwand: f.aufwand, risiken: f.risiken };
          const geaendert = Object.fromEntries(Object.entries(neu).filter(([k, v]) => v !== (alt as unknown as Record<string, unknown>)[k]));
          if (!Object.keys(geaendert).length) return;
          await api.patch(ziel(''), { ...geaendert, ...(trotzdem ? {} : { version: alt.version }) });
        }}
      />
      {uebernahme === 'projekt' && ziele && (
        <ProjektDialog idee={idee} ziele={ziele} schliessen={() => setUebernahme(null)} los={async (extra) => !!(await uebernehmen('projekt', extra))} />
      )}
      {(uebernahme === 'mindmap' || uebernahme === 'budget') && idee.projektId && (
        <UebernahmeDialog
          art={uebernahme}
          idee={idee}
          projektId={idee.projektId}
          schliessen={() => setUebernahme(null)}
          los={async (extra) => !!(await uebernehmen(uebernahme, extra))}
        />
      )}
    </article>
  );
}

/** Mindmap: unter welchen Punkt? Budget: welcher Betrag? */
function UebernahmeDialog({
  art,
  idee,
  projektId,
  schliessen,
  los,
}: {
  art: 'mindmap' | 'budget';
  idee: Idee;
  projektId: string;
  schliessen(): void;
  los(extra: Record<string, unknown>): Promise<boolean>;
}) {
  const [knoten, setKnoten] = useState<{ id: string; pfad: string }[] | null>(null);
  const [eltern, setEltern] = useState('');
  const [betrag, setBetrag] = useState(idee.kostenEuro ? String(idee.kostenEuro).replace('.', ',') : '');
  const [fehler, setFehler] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  useEffect(() => {
    if (art !== 'mindmap') return;
    api
      .get<{ id: string; pfad: string }[]>(pfad(`/mindmap-knoten?projekt=${projektId}`))
      .then(setKnoten)
      .catch(() => setKnoten([]));
  }, [art, projektId]);
  const ok = async () => {
    let extra: Record<string, unknown> = {};
    if (art === 'budget') {
      const n = zahlAus(betrag);
      if (n === null || Number.isNaN(n) || n <= 0) return setFehler('Bitte einen Betrag in Euro eingeben, z. B. 250.');
      extra = { betrag: n };
    } else extra = { elternId: eltern || null };
    setLaeuft(true);
    const fertig = await los(extra);
    setLaeuft(false);
    if (fertig) schliessen();
  };
  return (
    <Dialog
      titel={art === 'mindmap' ? 'Als Mindmap-Punkt übernehmen' : 'Als Kostenvorschlag ins Budget'}
      offen
      schliessen={schliessen}
      fuss={
        <>
          <Knopf art="haupt" disabled={laeuft} onClick={() => void ok()}>
            Übernehmen
          </Knopf>
          <Knopf onClick={schliessen}>Abbrechen</Knopf>
        </>
      }
    >
      <div className="stapel">
        <p className="leise">„{idee.titel}“</p>
        <Meldung>{fehler}</Meldung>
        {art === 'mindmap' ? (
          <Feld titel="Unter welchen Punkt?" hinweis="Ohne Auswahl landet die Idee unter der Mitte der ersten Karte. Beschreibung, Kosten und Risiken kommen als Notiz dazu.">
            <Auswahl value={eltern} onChange={(e) => setEltern(e.target.value)}>
              <option value="">– unter die Mitte –</option>
              {(knoten ?? []).map((k) => (
                <option key={k.id} value={k.id}>
                  {k.pfad}
                </option>
              ))}
            </Auswahl>
          </Feld>
        ) : (
          <Feld titel="Betrag in Euro" hinweis="Wird als Angebot (Vorschlag) im Budget abgelegt – dort bestätigt jemand.">
            <Eingabe value={betrag} onChange={(e) => setBetrag(e.target.value)} inputMode="decimal" autoFocus onKeyDown={(e) => e.key === 'Enter' && void ok()} />
          </Feld>
        )}
      </div>
    </Dialog>
  );
}

/**
 * Idee in ein Projekt übernehmen: in ein bestehendes (Schreibrecht in „Ideen“) oder ein neues Projekt
 * (nur wer Projekte anlegen darf; Name und Beschreibung aus der Idee). Die Idee wird dort als Kopie festgehalten.
 */
function ProjektDialog({
  idee,
  ziele,
  schliessen,
  los,
}: {
  idee: Idee;
  ziele: IdeenZiele;
  schliessen(): void;
  los(extra: Record<string, unknown>): Promise<boolean>;
}) {
  const andere = ziele.projekte.filter((p) => p.id !== idee.projektId);
  const [wahl, setWahl] = useState<string>(andere[0]?.id ?? (ziele.projektNeu ? 'neu' : ''));
  const [name, setName] = useState(idee.titel.slice(0, 120));
  const [beschreibung, setBeschreibung] = useState(
    [idee.beschreibung, idee.kosten && `Kosten (grob): ${idee.kosten}`, idee.aufwand && `Aufwand (grob): ${idee.aufwand}`, idee.risiken && `Risiken: ${idee.risiken}`]
      .filter(Boolean)
      .join('\n')
      .slice(0, 5000),
  );
  const [fehler, setFehler] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const navigate = useNavigate();
  const ok = async () => {
    setFehler('');
    setLaeuft(true);
    try {
      let projektId = wahl;
      if (wahl === 'neu') {
        if (!name.trim()) throw new Error('Das Projekt braucht einen Namen.');
        const p = await api.post<{ id: string }>('/api/projekte', { name: name.trim(), beschreibung });
        projektId = p.id;
      }
      if (!projektId) throw new Error('Bitte ein Projekt wählen.');
      if (await los({ projektId })) {
        schliessen();
        if (wahl === 'neu') navigate(`/p/${projektId}`);
      }
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Fehler');
    } finally {
      setLaeuft(false);
    }
  };
  return (
    <Dialog
      titel="In ein Projekt übernehmen"
      offen
      schliessen={schliessen}
      breit
      fuss={
        <>
          <Knopf art="haupt" disabled={laeuft || !wahl} onClick={() => void ok()}>
            {wahl === 'neu' ? 'Projekt anlegen und übernehmen' : 'Übernehmen'}
          </Knopf>
          <Knopf onClick={schliessen}>Abbrechen</Knopf>
        </>
      }
    >
      <div className="stapel">
        <p className="leise">„{idee.titel}“ wird im Projekt als festgehaltene Idee abgelegt (Unterreiter „Übernommene Ideen“). Die Idee hier bleibt.</p>
        <Meldung>{fehler}</Meldung>
        <Feld titel="Projekt">
          <Auswahl value={wahl} onChange={(e) => setWahl(e.target.value)}>
            {andere.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
            {ziele.projektNeu && <option value="neu">+ Neues Projekt aus dieser Idee</option>}
          </Auswahl>
        </Feld>
        {wahl === 'neu' && (
          <>
            <Feld titel="Name des neuen Projekts">
              <Eingabe value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoFocus />
            </Feld>
            <Feld titel="Beschreibung" hinweis="aus der Idee übernommen – du wirst Projektleitung">
              <Textfeld value={beschreibung} onChange={(e) => setBeschreibung(e.target.value)} rows={5} maxLength={5000} />
            </Feld>
          </>
        )}
        {!andere.length && !ziele.projektNeu && <p className="leise">Du hast kein Projekt, in dem du Ideen anlegen darfst.</p>}
      </div>
    </Dialog>
  );
}
